/**
 * Draw form items configuration.
 * Generates form field definitions for draw creation with validation and options.
 */
import {
  factoryConstants,
  drawDefinitionConstants,
  eventConstants,
  policyConstants,
  fixtures,
} from 'tods-competition-factory';
import { drawFormModel, DrawFormMode } from './drawFormModel';
import { acceptedEntriesCount } from './acceptedEntriesCount';
import { tournamentEngine } from 'services/factory/engine';
import { getDrawTypeOptions } from './getDrawTypeOptions';
import { getSeedingAllowance } from './seedingPolicies';
import { providerConfig } from 'config/providerConfig';
import { buildSeedCountOptions } from './seedCount';
import { validators } from 'courthive-components';
import { t } from 'i18n';

import POLICY_SCORING from 'assets/policies/scoringPolicy';
import {
  ADVANCE_PER_GROUP,
  AUTOMATED,
  BEST_FINISHERS,
  CUSTOM,
  DRAFT,
  DRAW_NAME,
  DRAW_SIZE,
  DRAW_TYPE,
  DYNAMIC_RATINGS,
  FIC_DEPTH,
  GROUP_REMAINING,
  GROUP_SIZE,
  MANUAL,
  MATCHUP_FORMAT,
  PLAYOFF_DRAW_TYPE,
  PLAYOFF_GROUP_SIZE,
  PLAYOFF_TYPE,
  POSITIONS,
  QUALIFYING_FIRST,
  QUALIFYING_POSITIONS,
  QUALIFIERS_COUNT,
  RATING_SCALE,
  ROUNDS_COUNT,
  SEEDING_POLICY,
  SEEDS_COUNT,
  STRUCTURE_NAME,
  TEAM_AVOIDANCE,
  TOP_FINISHERS,
  TOTAL_ADVANCE,
  WINNERS,
} from 'constants/tmxConstants';

const { MAIN, QUALIFYING, ROUND_ROBIN, ROUND_ROBIN_WITH_PLAYOFF, SINGLE_ELIMINATION } = drawDefinitionConstants;
const { DOMINANT_DUO, COLLEGE_DEFAULT, LAVER_CUP } = factoryConstants.tieFormatConstants;
const { POLICY_TYPE_SCORING, POLICY_TYPE_SEEDING } = policyConstants;
const { SINGLES } = factoryConstants.eventConstants;
const { TEAM } = eventConstants;

// Seeding policy constants
const CLUSTER = 'CLUSTER';
const SEPARATE = 'SEPARATE';
const INHERIT = 'INHERIT';

/** Qualifying-side mode kinds that drive isQualifying-derived UI flags. */
const QUALIFYING_KINDS = new Set(['NEW_QUALIFYING', 'GENERATE_QUALIFYING', 'ATTACH_QUALIFYING']);

export function getDrawFormItems({ event, mode }: { event: any; mode: DrawFormMode }): {
  structurePositionAssignments: any;
  items: any[];
} {
  const modelView = drawFormModel(mode, {});
  const fs = modelView.fieldStates;
  const qualifiersCount = modelView.derivedValues.qualifiersCount;
  const structurePositionAssignments = modelView.derivedValues.structurePositionAssignments;
  const drawSize = modelView.derivedValues.drawSize;
  const maxDrawSize = Math.max(drawSize, 512);
  const drawId = 'draw' in mode ? mode.draw?.drawId : undefined;
  const drawsCount = event.drawDefinitions?.length || 0;
  const drawType = SINGLE_ELIMINATION;
  const isQualifyingMode = QUALIFYING_KINDS.has(mode.kind);

  // An attached policy lives in the `appliedPolicies` EXTENSION, not in a `policyDefinitions`
  // property — `attachPolicies` goes through `addExtension` and never writes such a property, so the
  // direct reads this used to do could not see one. That mattered more than a missing menu entry:
  // with no "Inherited" option the form defaulted to a positioning preset, and a preset is sent as
  // `policyDefinitions`, which the factory prefers over the attached policy
  // (`validateAndDeriveDrawValues`: `policyDefinitions?.[seeding] ?? appliedPolicies?.[seeding]`).
  // A tournament carrying its governing body's seeding rules would have had them silently
  // overridden by every draw generated from this form.
  const hasExistingPolicy = !!tournamentEngine.getPolicyDefinitions({
    policyTypes: [POLICY_TYPE_SEEDING],
    event,
  })?.policyDefinitions?.[POLICY_TYPE_SEEDING];

  const flightProfile = tournamentEngine.q.flightProfile({ event });
  const flight = flightProfile?.flights?.find((f: any) => f.drawId === drawId);

  const allowedFormats = providerConfig.getAllowedList('allowedMatchUpFormats');
  const scoreFormatOptions = [
    {
      label: t('drawers.addDraw.custom'),
      value: CUSTOM,
    },
  ].concat(
    POLICY_SCORING[POLICY_TYPE_SCORING].matchUpFormats
      .filter(({ matchUpFormat }: any) => !allowedFormats.length || allowedFormats.includes(matchUpFormat))
      .map(({ matchUpFormat, description: label }: any) => ({
        selected: matchUpFormat === 'SET3-S:6/TB7',
        value: matchUpFormat,
        label,
      })),
  );

  const tieFormatOptions = [
    { label: t('drawers.addDraw.dominantDuo'), value: DOMINANT_DUO, selected: true },
    { label: t('drawers.addDraw.collegeDefault'), value: COLLEGE_DEFAULT },
    { label: t('drawers.addDraw.laverCup'), value: LAVER_CUP },
    { label: t('drawers.addDraw.custom'), value: CUSTOM },
  ];

  // A provider that declares a seeding policy AND withholds canModifyPolicies is stating a rule.
  // Offering the two positioning presets here would let an operator override it per draw, which is
  // the one thing compliance does not permit — so the selector collapses to a single entry.
  const seedingPolicyOptions = providerConfig.isSeedingPolicyLocked()
    ? [{ label: t('drawers.addDraw.providerSeedingPolicy'), value: INHERIT, selected: true }]
    : [
        ...(hasExistingPolicy ? [{ label: t('drawers.addDraw.inherited'), value: INHERIT, selected: true }] : []),
        { label: t('drawers.addDraw.separatedUsta'), value: SEPARATE, selected: !hasExistingPolicy },
        { label: t('drawers.addDraw.adjacentItf'), value: CLUSTER },
      ];
  // `acceptedEntriesCount` is the same count `updateDrawSize` feeds the selector on every later
  // interaction. Rendering from a different notion of "how many entries" (drawEntries includes
  // cross-stage entries that never occupy a position) made the initial list disagree with itself
  // the moment anything on the form was touched.
  const seedCountParticipantsCount = acceptedEntriesCount({
    drawId,
    event,
    stage: isQualifyingMode ? QUALIFYING : MAIN,
  });
  const seedCountOptions = buildSeedCountOptions({
    ...getSeedingAllowance({
      participantsCount: seedCountParticipantsCount,
      eventId: event.eventId,
      drawSize,
    }),
    participantsCount: seedCountParticipantsCount,
    drawType,
    drawSize,
  }).map((option, index) => (index ? option : { ...option, selected: true }));

  const { validGroupSizes = [] } = tournamentEngine.getValidGroupSizes({ drawSize: 32, groupSizeLimit: 8 });
  const roundRobinOptions = validGroupSizes.map((size: number) => ({ label: size, value: size }));
  const playoffOptions = [
    { label: t('drawers.addDraw.groupWinners'), value: WINNERS },
    { label: t('drawers.addDraw.groupPositions'), value: POSITIONS },
    { label: t('drawers.addDraw.topFinishers'), value: TOP_FINISHERS },
    { label: t('drawers.addDraw.bestFinishers'), value: BEST_FINISHERS },
  ];
  const advanceOptions = [
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
  ];

  // disableAutomated was `!!structureId && !isAttachingQualifying` — always
  // false for every real mode (structureId only present in ATTACH_QUALIFYING,
  // where isAttachingQualifying is also true). Simplified to a constant.
  const allCreationOptions = [
    { label: AUTOMATED, value: AUTOMATED, selected: true },
    { label: DRAFT, value: DRAFT },
    { label: MANUAL, value: false },
  ];

  // Filter creation methods by provider config
  const allowedMethods = providerConfig.getAllowedList('allowedCreationMethods');
  const creationOptions = allowedMethods.length
    ? allCreationOptions.filter((opt) => {
        const val = opt.value === false ? MANUAL : String(opt.value);
        return allowedMethods.includes(val);
      })
    : allCreationOptions.filter((opt) => {
        // Also respect individual permission flags
        if (opt.value === DRAFT && !providerConfig.isAllowed('canUseDraftPositioning')) return false;
        if (opt.value === false && !providerConfig.isAllowed('canUseManualPositioning')) return false;
        return true;
      });

  // Discover available rating scales for DrawMatic configuration
  const { ratingsParameters } = fixtures;
  const { participants: allParticipants = [] } = tournamentEngine.getParticipants({ withScaleValues: true }) ?? {};
  const presentRatings = new Set<string>();
  for (const p of allParticipants) {
    for (const item of p.ratings?.[SINGLES] || []) {
      presentRatings.add(item.scaleName);
    }
  }
  const activeRatingKeys = Object.keys(ratingsParameters).filter(
    (key) => !(ratingsParameters as any)[key].deprecated && presentRatings.has(key),
  );
  const ratingScaleOptions = [
    { label: t('drawers.addDraw.offOption'), value: '', selected: true },
    ...activeRatingKeys.map((key) => ({ label: key, value: key.toLowerCase() })),
  ];

  const roundsCountOptions = [
    { label: '1', value: 1, selected: true },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
  ];

  const items = [
    {
      error: t('drawers.addDraw.minCharsError'),
      placeholder: t('drawers.addDraw.structureNamePlaceholder'),
      validator: validators.nameValidator(4),
      label: t('drawers.addDraw.structureName'),
      value: fs[STRUCTURE_NAME]?.value ?? 'Qualifying',
      field: STRUCTURE_NAME,
      selectOnFocus: true,
      visible: fs[STRUCTURE_NAME]?.visible ?? false,
    },
    {
      label: t('drawers.addDraw.qualifyingFirst'),
      field: QUALIFYING_FIRST,
      id: QUALIFYING_FIRST,
      checkbox: true,
      hide: !fs[QUALIFYING_FIRST]?.visible,
    },
    {
      error: t('drawers.addDraw.minCharsError'),
      placeholder: t('drawers.addDraw.drawNamePlaceholder'),
      value: flight?.drawName || `Draw ${drawsCount + 1}`,
      validator: validators.nameValidator(4),
      selectOnFocus: true,
      visible: fs[DRAW_NAME]?.visible ?? false,
      label: t('drawers.addDraw.drawName'),
      field: DRAW_NAME,
      focus: true,
    },
    {
      options: getDrawTypeOptions({ isQualifying: isQualifyingMode }),
      label: t('drawers.addDraw.drawType'),
      field: DRAW_TYPE,
      value: drawType,
    },
    {
      error: t('drawers.addDraw.drawSizeError', { max: maxDrawSize }),
      validator: validators.numericRange(2, maxDrawSize),
      selectOnFocus: true,
      label: t('drawers.addDraw.drawSize'),
      value: drawSize,
      field: DRAW_SIZE,
    },
    {
      visible: ([ROUND_ROBIN, ROUND_ROBIN_WITH_PLAYOFF] as string[]).includes(drawType),
      options: roundRobinOptions,
      label: t('drawers.addDraw.groupSize'),
      field: GROUP_SIZE,
      value: 4,
    },
    {
      visible: ([ROUND_ROBIN_WITH_PLAYOFF] as string[]).includes(drawType),
      options: playoffOptions,
      label: t('drawers.addDraw.playoffType'),
      field: PLAYOFF_TYPE,
    },
    {
      visible: ([ROUND_ROBIN_WITH_PLAYOFF] as string[]).includes(drawType),
      options: getDrawTypeOptions({ isPlayoff: true }),
      label: t('drawers.addDraw.playoffDrawType'),
      field: PLAYOFF_DRAW_TYPE,
    },
    {
      visible: false,
      options: roundRobinOptions,
      label: t('drawers.addDraw.playoffGroupSize'),
      field: PLAYOFF_GROUP_SIZE,
      value: 4,
    },
    {
      label: t('drawers.addDraw.advancePerGroup'),
      options: advanceOptions,
      field: ADVANCE_PER_GROUP,
      visible: false,
    },
    {
      label: t('drawers.addDraw.totalToAdvance'),
      field: TOTAL_ADVANCE,
      validator: validators.numericValidator,
      selectOnFocus: true,
      visible: false,
      value: 4,
    },
    {
      label: t('drawers.addDraw.secondPlayoffFromRemaining'),
      field: GROUP_REMAINING,
      id: GROUP_REMAINING,
      checkbox: true,
      visible: false,
    },
    {
      help: { text: t('drawers.addDraw.automationDisabled'), visible: false },
      options: creationOptions,
      label: t('drawers.addDraw.creation'),
      field: AUTOMATED,
      value: '',
    },
    {
      options: roundsCountOptions,
      label: t('drawers.addDraw.roundsCount'),
      field: ROUNDS_COUNT,
      value: 1,
      visible: false,
    },
    {
      options: ratingScaleOptions,
      label: t('drawers.addDraw.ratingScale'),
      field: RATING_SCALE,
      visible: false,
    },
    {
      label: t('drawers.addDraw.dynamicRatings'),
      field: DYNAMIC_RATINGS,
      id: DYNAMIC_RATINGS,
      checkbox: true,
      visible: false,
    },
    {
      label: t('drawers.addDraw.teamAvoidance'),
      field: TEAM_AVOIDANCE,
      id: TEAM_AVOIDANCE,
      checkbox: true,
      visible: false,
    },
    {
      options: [
        { label: t('round_names.F'), value: 'F', selected: true },
        { label: t('round_names.SF'), value: 'SF' },
        { label: t('round_names.QF'), value: 'QF' },
        { label: t('addDrawForm.roundOf16'), value: 'R16' },
      ],
      label: t('addDrawForm.consolationFeedDepth'),
      field: FIC_DEPTH,
      visible: false,
    },
    {
      hide: event.eventType === TEAM,
      options: scoreFormatOptions,
      field: MATCHUP_FORMAT,
      label: t('drawers.addDraw.scoreFormat'),
      value: '',
    },
    {
      options: seedingPolicyOptions,
      visible: fs[SEEDING_POLICY]?.visible ?? true,
      field: SEEDING_POLICY,
      label: t('drawers.addDraw.seedingPolicy'),
      value: '',
    },
    {
      options: seedCountOptions,
      visible: fs[SEEDS_COUNT]?.visible ?? true,
      field: SEEDS_COUNT,
      label: t('drawers.addDraw.seedsCount'),
      value: '',
    },
    {
      hide: event.eventType !== TEAM,
      options: tieFormatOptions,
      field: 'tieFormatName',
      label: t('drawers.addDraw.scorecard'),
      value: '',
    },
    {
      validator: validators.numericValidator,
      field: QUALIFIERS_COUNT,
      value: qualifiersCount,
      selectOnFocus: true,
      label: t('drawers.addDraw.qualifiers'),
    },
    {
      validator: validators.numericValidator,
      field: QUALIFYING_POSITIONS,
      value: 4,
      selectOnFocus: true,
      label: t('drawers.addDraw.qualifyingPositions'),
      visible: false,
    },
  ];

  return { items, structurePositionAssignments };
}
